import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { closeAccountAsAdmin, findAccounts, needsFreshSignIn, restoreAccount } from './admin.service';
import { AdminAccountDto } from './dtos/admin-account.dto';
import { formatEraseOn, previewEraseOn } from '../settings/account-closure.service';
import { isAuthRedirectError } from '../../shared/services/auth.service';
import { logError } from '../../shared/services/logger.service';
import { displayPhone } from '../../shared/phone';
import { ConfirmItsYou } from '../settings/ConfirmItsYou';

type PendingAction = { account: AdminAccountDto; action: 'close' | 'restore' };

const displayName = (account: AdminAccountDto) =>
  account.name?.trim()
  || account.email
  || (account.phoneNumber ? displayPhone(account.phoneNumber) : null)
  || `Account ${account.userId}`;

const statusLine = (account: AdminAccountDto) => account.closedAt
  ? `Closed on ${formatEraseOn(account.closedAt)} · deleted on ${formatEraseOn(account.eraseOn ?? account.eraseAfter ?? '')}`
  : 'Open';

// Support's tools for Delete account: find an account by email or phone,
// restore one closed by mistake before it is erased, or close one for a
// person who asked by email. Closing here has the same effect as Delete
// account in the apps. Like it, closing and restoring need a sign-in from
// the last few minutes: when the server asks, the dialog confirms it's the
// admin and then carries on.
const AccountsTab: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState<string | null>(null);
  const [results, setResults] = useState<AdminAccountDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  // pending outlives the dialog's open flag so its text holds while it fades out.
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [acting, setActing] = useState(false);
  const [reauthenticating, setReauthenticating] = useState(false);
  const [signingInAgain, setSigningInAgain] = useState(false);

  const ask = (account: AdminAccountDto, action: PendingAction['action']) => {
    setPending({ account, action });
    setSigningInAgain(false);
    setConfirming(true);
  };

  const failed = (context: string, e: unknown) => {
    if (isAuthRedirectError(e)) {
      return;
    }
    logError(context, {}, e as Error);
    enqueueSnackbar((e as Error).message, { variant: 'error', autoHideDuration: 8000 });
  };

  const search = async (text: string) => {
    setSearching(true);
    try {
      setResults(await findAccounts(navigate, text));
      setSearched(text);
    } catch (e) {
      failed('Account search failed', e);
    } finally {
      setSearching(false);
    }
  };

  const handleFind = (event: React.FormEvent) => {
    event.preventDefault();
    const text = query.trim();
    if (text) {
      void search(text);
    }
  };

  const confirmPending = async () => {
    if (!pending || !confirming || acting) {
      return;
    }
    const { account, action } = pending;
    const name = displayName(account);
    setActing(true);
    try {
      if (action === 'close') {
        await closeAccountAsAdmin(navigate, account.userId);
        enqueueSnackbar(`Closed ${name}'s account.`, { variant: 'success' });
      } else {
        const restored = await restoreAccount(navigate, account.userId);
        if (restored.signInEnabled) {
          enqueueSnackbar(`Restored ${name}'s account.`, { variant: 'success' });
        } else {
          enqueueSnackbar(
            `Restored ${name}'s account, but their sign-in is still turned off. Turn it on in the Firebase console (Authentication → Users).`,
            { variant: 'warning', autoHideDuration: 15000 });
        }
      }
      setConfirming(false);
      if (searched) {
        await search(searched);
      }
    } catch (e) {
      if (needsFreshSignIn(e)) {
        setSigningInAgain(true);
        return;
      }
      failed(action === 'close' ? 'Admin account close failed' : 'Account restore failed', e);
    } finally {
      setActing(false);
    }
  };

  const pendingName = pending ? displayName(pending.account) : '';

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Box component="form" onSubmit={handleFind} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
        <TextField
          label="Email or phone number"
          value={query}
          onChange={event => setQuery(event.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
        />
        <Button type="submit" variant="contained" disabled={searching || !query.trim()}>
          {searching ? <CircularProgress size={20} color="inherit" /> : 'Find'}
        </Button>
      </Box>

      {results?.length === 0 && (
        <Typography color="text.secondary">No account uses that email or phone number.</Typography>
      )}

      <Stack spacing={2}>
        {results?.map(account => (
          <Card key={account.userId} variant="outlined">
            <CardContent>
              <Typography variant="h6" component="h2">{displayName(account)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[account.email, account.phoneNumber ? displayPhone(account.phoneNumber) : null].filter(Boolean).join(' · ')}
              </Typography>
              {account.teams.length > 0 && (
                <Typography variant="body2" color="text.secondary">{account.teams.join(', ')}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>{statusLine(account)}</Typography>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
              {account.closedAt ? (
                <Button variant="contained" onClick={() => ask(account, 'restore')}>Restore</Button>
              ) : (
                <Button variant="outlined" color="error" onClick={() => ask(account, 'close')}>
                  Close account
                </Button>
              )}
            </CardActions>
          </Card>
        ))}
      </Stack>

      <Dialog
        open={confirming}
        onClose={() => !acting && !reauthenticating && setConfirming(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {signingInAgain
            ? "Confirm it's you"
            : pending?.action === 'close' ? `Close ${pendingName}'s account?` : `Restore ${pendingName}'s account?`}
        </DialogTitle>
        {signingInAgain ? (
          <ConfirmItsYou
            onConfirmed={() => {
              setSigningInAgain(false);
              void confirmPending();
            }}
            onBack={() => setSigningInAgain(false)}
            onBusyChange={setReauthenticating}
            recaptchaId="admin-account-recaptcha"
          />
        ) : (
          <>
            <DialogContent>
              <DialogContentText>
                {pending?.action === 'close'
                  ? `They're signed out and removed from every team now, and their personal details are deleted on ${previewEraseOn()}. Only do this when they asked from the email or phone number on the account.`
                  : 'They can sign in again and are active again on the teams they left when they closed it.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setConfirming(false)} disabled={acting} color="inherit">Cancel</Button>
              <Button
                variant="contained"
                color={pending?.action === 'close' ? 'error' : 'primary'}
                onClick={confirmPending}
                disabled={acting}
              >
                {pending?.action === 'close' ? 'Close account' : 'Restore'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AccountsTab;
